import { seamKey, sortedCrossings, sortedSeams } from "./layout.ts";
import { validateDocument } from "./layout-rules.ts";
import { conflict, mergeKeyed, mergeObject, type MergeRegion, type MergeTally } from "./merge-rules.ts";
import type { MapDocument, MapGroup, Seam, SeamCrossing, WorldDocument } from "./types.ts";

/**
 * The three-way merge of a world's layout (ADR-086 decision 6, ADR-109 decision 6): map groups merge by id, seams
 * by their pair and a seam's crossings by name, each field of one as one value. A merge whose layout breaks a
 * rule of `validateDocument` (two maps placed on one spot, a seam naming a map the other side removed, a map in
 * a group the other side removed) is settled by `settleLayout` from one side's layout, so a merged document is
 * never one the reader refuses.
 */

/**
 * A seam or a crossing carrying the key `mergeKeyed` reads.
 */
type Keyed<T> = T & { readonly id: string };

/**
 * Merges the map groups by id.
 *
 * @param tally The tally.
 * @param base The base's groups, undefined for none.
 * @param mine Mine.
 * @param theirs Theirs.
 * @param region The world's region.
 * @returns The merged groups, undefined when none is left.
 */
export function mergeMapGroups(tally: MergeTally, base: unknown, mine: unknown, theirs: unknown, region: () => MergeRegion): MapGroup[] | undefined {
  const groups = mergeKeyed<MapGroup>(tally, (base ?? []) as readonly MapGroup[], (mine ?? []) as readonly MapGroup[], (theirs ?? []) as readonly MapGroup[], {
    region,
    content: (b, m, t) => mergeObject(tally, b, m, t, region),
    orderRegion: region,
  });
  return groups.length === 0 ? undefined : groups;
}

/**
 * Seams keyed by their pair, each crossing keyed by its name.
 *
 * @param seams The seams, undefined for none.
 * @returns The keyed seams.
 */
function keyedSeams(seams: unknown): Keyed<Seam>[] {
  return ((seams ?? []) as readonly Seam[]).map((seam) => ({ ...seam, id: seamKey(seam), crossings: seam.crossings.map((crossing) => ({ ...crossing, id: crossing.name })) }));
}

/**
 * A crossing without the key the merge gave it.
 *
 * @param crossing The keyed crossing.
 * @returns The crossing.
 */
function unkeyedCrossing(crossing: Keyed<SeamCrossing>): SeamCrossing {
  const { id: _id, ...rest } = crossing;
  return rest;
}

/**
 * Merges the seams by pair, a seam's state and barrier each as one value and its crossings by name.
 *
 * @param tally The tally.
 * @param base The base's seams, undefined for none.
 * @param mine Mine.
 * @param theirs Theirs.
 * @param region The world's region.
 * @returns The merged seams in key order, undefined when none is left.
 */
export function mergeSeams(tally: MergeTally, base: unknown, mine: unknown, theirs: unknown, region: () => MergeRegion): Seam[] | undefined {
  const merged = mergeKeyed<Keyed<Seam>>(tally, keyedSeams(base), keyedSeams(mine), keyedSeams(theirs), {
    region,
    content: (b, m, t) =>
      mergeObject(tally, b, m, t, region, {
        crossings: (bc, mc, tc) =>
          mergeKeyed<Keyed<SeamCrossing>>(tally, bc as readonly Keyed<SeamCrossing>[], mc as readonly Keyed<SeamCrossing>[], tc as readonly Keyed<SeamCrossing>[], {
            region,
            content: (bx, mx, tx) => mergeObject(tally, bx, mx, tx, region),
            orderRegion: region,
          }),
      }),
    orderRegion: region,
  });
  const seams = merged.map(({ id: _id, ...seam }) => ({ ...seam, crossings: sortedCrossings((seam.crossings as readonly Keyed<SeamCrossing>[]).map(unkeyedCrossing)) }));
  return seams.length === 0 ? undefined : sortedSeams(seams);
}

/**
 * Whether a document keeps every layout rule.
 *
 * @param world The document.
 * @returns True when `validateDocument` accepts it.
 */
function valid(world: WorldDocument): boolean {
  try {
    validateDocument(world);
    return true;
  } catch {
    return false;
  }
}

/**
 * A map with its layout fields (its place, host, sharing, capacity, atmosphere, group and draft mark) taken
 * from another version of it, or dropped where that version has none.
 *
 * @param map The merged map.
 * @param from The version to take them from.
 * @returns The map.
 */
function layoutFrom(map: MapDocument, from: MapDocument): MapDocument {
  const { place: _place, host: _host, share: _share, capacity: _capacity, atmosphere: _atmosphere, mapGroup: _group, draft: _draft, ...rest } = map;
  return {
    ...rest,
    ...(from.place === undefined ? {} : { place: from.place }),
    ...(from.host === undefined ? {} : { host: from.host }),
    ...(from.share === undefined ? {} : { share: from.share }),
    ...(from.capacity === undefined ? {} : { capacity: from.capacity }),
    ...(from.atmosphere === undefined ? {} : { atmosphere: from.atmosphere }),
    ...(from.mapGroup === undefined ? {} : { mapGroup: from.mapGroup }),
    ...(from.draft === true ? { draft: true as const } : {}),
  };
}

/**
 * Keeps a merged document readable. When its layout breaks a rule, the world's region is told as a conflict and
 * the layout is taken whole from the side its choice settles it to, mine when none is made: each side was read
 * or built by commands that check every rule, so its layout keeps them. That side gives the grid, the groups,
 * the seams that name maps the merge still holds, and each map's place, host, sharing, capacity, atmosphere,
 * group and draft mark as it has them; a map only the other side holds keeps its place, its host with its
 * sharing, and its group where each still fits, and loses the one that does not.
 *
 * @param merged The merged document.
 * @param sides Mine and theirs.
 * @param tally The tally.
 * @param region The world's region.
 * @returns The merged document when it keeps every rule, the settled one otherwise.
 */
export function settleLayout(merged: WorldDocument, sides: { readonly mine: WorldDocument; readonly theirs: WorldDocument }, tally: MergeTally, region: () => MergeRegion): WorldDocument {
  if (valid(merged)) {
    return merged;
  }
  const chosen = conflict(tally, region()) === "mine" ? sides.mine : sides.theirs;
  const held = new Set(merged.maps.map((map) => map.id));
  const seams = (chosen.seams ?? []).filter((seam) => held.has(seam.a) && held.has(seam.b));
  const { grid: _grid, mapGroups: _groups, seams: _seams, ...rest } = merged;
  let settled: WorldDocument = {
    ...rest,
    ...(chosen.grid === undefined ? {} : { grid: chosen.grid }),
    ...(chosen.mapGroups === undefined ? {} : { mapGroups: chosen.mapGroups }),
    maps: merged.maps.map((map) => {
      const { place: _place, host: _host, share: _share, mapGroup: _group, ...bare } = map;
      return layoutFrom(map, chosen.maps.find((m) => m.id === map.id) ?? bare);
    }),
    ...(seams.length === 0 ? {} : { seams }),
  };
  for (const map of merged.maps) {
    if (chosen.maps.some((m) => m.id === map.id)) {
      continue;
    }
    for (const fields of [["place"], ["host", "share"], ["mapGroup"]] as const) {
      if (map[fields[0]] === undefined) {
        continue;
      }
      const taken = Object.fromEntries(fields.flatMap((field) => (map[field] === undefined ? [] : [[field, map[field]]])));
      const tried: WorldDocument = { ...settled, maps: settled.maps.map((m) => (m.id === map.id ? { ...m, ...taken } : m)) };
      if (valid(tried)) {
        settled = tried;
      }
    }
  }
  return settled;
}
